import { Link, useNavigate } from 'react-router-dom'

import type { RfqUpdateDto } from '../api/rfqUpdates'
import { useRfqNotifications } from '../notifications/RfqNotificationsProvider'
import { MarketEmptyState } from './MarketEmptyState'
import { formatDate } from '../screens/ordersUtils'

type NotificationsFeedProps = {
  compact?: boolean
  limit?: number
  onNavigate?: () => void
}

export const NotificationsFeed = ({ compact = false, limit, onNavigate }: NotificationsFeedProps) => {
  const navigate = useNavigate()
  const { items, unreadCount, loading, error, markRead } = useRfqNotifications()

  const handleOpenUpdate = async (update: RfqUpdateDto) => {
    if (!update.read) {
      await markRead(update.id)
    }
    onNavigate?.()
    navigate(`/orders?rfqId=${update.rfqId}`)
  }

  const visibleItems = limit != null ? items.slice(0, limit) : items

  if (loading) {
    return <p className="echat-muted">{compact ? 'Загрузка…' : 'Загрузка уведомлений…'}</p>
  }

  if (error) {
    return (
      <p className="echat-error" role="alert">
        {error}
      </p>
    )
  }

  if (visibleItems.length === 0) {
    return (
      <MarketEmptyState
        title="Новых обновлений нет"
        description="Когда поставщик изменит статус заявки, уведомление появится здесь."
        actions={compact ? undefined : [{ label: 'К заявкам', to: '/orders', variant: 'ghost' }]}
      />
    )
  }

  return (
    <>
      {compact && unreadCount > 0 ? (
        <p className="market-notifications-panel__summary">{unreadCount} непрочитанных</p>
      ) : null}
      <div className={`echat-inbox market-notifications-feed${compact ? ' market-notifications-feed--compact' : ''}`}>
        <ul className="echat-inbox__list">
          {visibleItems.map((update) => (
            <li key={update.id} className="echat-inbox__item">
              <button
                type="button"
                className={
                  update.read
                    ? 'echat-inbox__row market-notifications-row'
                    : 'echat-inbox__row market-notifications-row market-notifications-row--unread'
                }
                onClick={() => void handleOpenUpdate(update)}
              >
                {!update.read ? <span className="market-notifications-row__dot" aria-hidden="true" /> : null}
                <span className="echat-inbox__body">
                  <span className="echat-inbox__name">{update.message}</span>
                  <span className="echat-inbox__preview market-inbox-row__meta">
                    {!update.read ? <span className="echat-badge echat-badge--info">Новое</span> : null}
                    <span className="echat-muted">
                      {[update.supplierOrganizationName, update.createdAt ? formatDate(update.createdAt) : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </span>
              </button>
              {!update.read && !compact ? (
                <button
                  type="button"
                  className="market-notifications-row__mark-read echat-btn echat-btn--ghost echat-btn--sm"
                  onClick={(event) => {
                    event.stopPropagation()
                    void markRead(update.id)
                  }}
                >
                  Прочитано
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
      {compact && items.length > (limit ?? 0) ? (
        <div className="market-notifications-panel__footer">
          <Link to="/orders" className="echat-btn echat-btn--ghost echat-btn--sm" onClick={onNavigate}>
            Все заявки
          </Link>
        </div>
      ) : null}
    </>
  )
}
